import { useEffect, useRef, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import { Plus, Boxes, X, Trash2, Pencil } from "lucide-react";

export default function Datasets() {
  const { user } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [demoDatasets, setDemoDatasets] = useState([]);
  const [show, setShow] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [editingDataset, setEditingDataset] = useState(null);

  const load = () => api.get("/datasets").then((r) => {
    setDatasets(r.data.datasets || []);
    setDemoDatasets(r.data.demo_datasets || []);
  });
  useEffect(() => {
    load();
    try {
      const raw = sessionStorage.getItem("camaroo:new_dataset_hint");
      if (raw) {
        sessionStorage.removeItem("camaroo:new_dataset_hint");
        setPrefill(JSON.parse(raw));
        setShow(true);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const remove = async (id) => {
    if (!confirm("Delete dataset?")) return;
    try { await api.delete(`/datasets/${id}`); load(); } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Datasets</h1>
            <p>Reusable analytics models built from approved tables, fields, joins, SQL, and uploaded Excel data.</p>
          </div>

          {canAtLeast(user, "analyst") && (
            <button className="erp-primary-btn" onClick={() => setShow(true)} data-testid="ds-new">
              <Plus size={14} strokeWidth={1.8} /> New dataset
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="card-flat p-4">
            <div className="section-title mb-2">Demo datasets</div>
            <div className="section-sub mb-3">Camaroo demo data — usable directly by the dashboard builder.</div>
            <table className="tbl" data-testid="demo-datasets-table">
              <thead><tr><th>Name</th><th>Key</th><th>Fields</th><th>Type</th></tr></thead>
              <tbody>
                {demoDatasets.map((d) => (
                  <tr key={d.id}>
                    <td><Boxes size={14} strokeWidth={1.7} className="inline mr-1.5" />{d.name}</td>
                    <td className="font-mono text-[12px]">{d.id}</td>
                    <td>{(d.fields || []).length} fields</td>
                    <td><span className="pill warn">Demo</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-flat p-4">
            <div className="section-title mb-2">Workspace datasets</div>
            <table className="tbl" data-testid="datasets-table">
              <thead><tr><th>Name</th><th>Connection</th><th>Base table</th><th>Updated</th><th></th></tr></thead>
              <tbody>
                {datasets.map((d) => (
                  <tr key={d.id} data-testid={`dataset-row-${d.id}`}>
                    <td>
                      <Boxes size={14} strokeWidth={1.7} className="inline mr-1.5" />{d.name}
                      {d.kind === "excel" && (
                        <span className="pill ml-2" style={{ background: "#fef3c7", color: "#854d0e" }} title="Uploaded Excel data — refresh from Excel Uploads page" data-testid={`excel-badge-${d.id}`}>EXCEL</span>
                      )}
                    </td>
                    <td className="font-mono text-[12px]">{d.connection_id}</td>
                    <td className="font-mono text-[12px]">{d.kind === "excel" ? (d.source_meta?.filename || "Excel file") : (d.base_table || (d.custom_sql ? "custom SQL" : "—"))}</td>
                    <td className="numeric text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{new Date(d.updated_at).toLocaleString()}</td>
                    <td className="text-right">
                      {canAtLeast(user, "analyst") && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            className="btn btn-ghost"
                            onClick={() => {
                              setEditingDataset(d);
                              setPrefill(null);
                              setShow(true);
                            }}
                            title="Edit dataset"
                            data-testid={`dataset-edit-${d.id}`}
                          >
                            <Pencil size={13} strokeWidth={1.7} />
                          </button>
                          <button
                            className="btn btn-ghost"
                            onClick={() => remove(d.id)}
                            title="Delete dataset"
                          >
                            <Trash2 size={13} strokeWidth={1.7} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {datasets.length === 0 && <tr><td colSpan={5} className="text-center py-5" style={{ color: "var(--c-text-muted)" }}>No custom datasets. Use the demo datasets above or create one.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {show && (
          <NewDatasetModal
            onClose={() => {
              setShow(false);
              setPrefill(null);
              setEditingDataset(null);
              load();
            }}
            prefill={prefill}
            dataset={editingDataset}
          />
        )}
      </main>
    </div>
  );
}

// In-memory cache of discovered tables per connection (survives modal close within a session)
const TABLES_CACHE = new Map(); // connection_id -> [{schema, name, type}]

function NewDatasetModal({ onClose, prefill, dataset }) {
  const [conns, setConns] = useState([]);
  const [tables, setTables] = useState([]); // [{schema, name, type}]
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [columns, setColumns] = useState([]); // columns of selected base_table
  const [joinedColumns, setJoinedColumns] = useState({}); // table name -> columns
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState("");
  const isEditing = !!dataset?.id;
  const isExcelDataset = dataset?.kind === "excel";
  const skipInitialConnectionReset = useRef(isEditing);

  const [form, setForm] = useState({
    name: dataset?.name || (prefill?.base_table ? `${prefill.base_table} dataset` : ""),
    connection_id: dataset?.connection_id || prefill?.connection_id || "",
    base_table: dataset?.base_table || prefill?.base_table || "",
    base_schema: dataset?.base_schema || prefill?.schema || "",
    fields: Array.isArray(dataset?.fields) ? dataset.fields : [],
    joins: Array.isArray(dataset?.joins) ? dataset.joins : [],
    description: dataset?.description || "",
    custom_sql: dataset?.custom_sql || "",
  });
  const [busy, setBusy] = useState(false);

  const PAGE_SIZE = 50;

  useEffect(() => {
    api.get("/connections").then((r) => setConns((r.data.connections || []).filter((c) => c.source_type !== "demo")));
  }, []);

  // Load the saved table columns when editing an existing dataset.
  useEffect(() => {
    if (isExcelDataset || !isEditing || !form.connection_id || !form.base_table) return;

    setColumnsLoading(true);
    setColumnsError("");

    api.get(
      `/connections/${form.connection_id}/tables/${encodeURIComponent(form.base_table)}/columns`,
      { params: form.base_schema ? { schema: form.base_schema } : {} }
    )
      .then((r) => setColumns(r.data?.columns || []))
      .catch((e) => setColumnsError(e?.response?.data?.detail || e.message))
      .finally(() => setColumnsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Load columns for saved joined tables while editing.
  useEffect(() => {
    if (isExcelDataset || !isEditing || !form.connection_id || !form.joins?.length) return;

    const tableNames = [...new Set(form.joins.map((j) => j.right_table).filter(Boolean))];

    Promise.all(
      tableNames.map(async (tableName) => {
        const tableMeta = tables.find((t) => t.name === tableName);
        const response = await api.get(
          `/connections/${form.connection_id}/tables/${encodeURIComponent(tableName)}/columns`,
          { params: tableMeta?.schema ? { schema: tableMeta.schema } : {} }
        );
        return [tableName, response.data?.columns || []];
      })
    )
      .then((entries) => setJoinedColumns(Object.fromEntries(entries)))
      .catch((e) => setColumnsError(e?.response?.data?.detail || e.message));
  }, [isEditing, form.connection_id, form.joins, tables]);

  // If a prefill was provided, once tables have loaded auto-select the requested base_table
  useEffect(() => {
    if (!prefill?.base_table || !tables.length) return;
    if (form.base_table === prefill.base_table && columns.length) return;
    const t = tables.find((x) => x.name === prefill.base_table);
    if (!t) return;
    // mimic selectTable inline (loadColumns)
    setForm((s) => ({ ...s, base_table: t.name, base_schema: t.schema || s.base_schema, fields: [] }));
    setColumns([]); setColumnsError(""); setColumnsLoading(true);
    api.get(`/connections/${form.connection_id}/tables/${encodeURIComponent(t.name)}/columns`, { params: t.schema ? { schema: t.schema } : {} })
      .then((r) => setColumns(r.data?.columns || []))
      .catch((e) => setColumnsError(e?.response?.data?.detail || e.message))
      .finally(() => setColumnsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, prefill]);

  // Auto-load tables (names only) when connection changes; reuse cache.
  useEffect(() => {
    const connId = form.connection_id;
    if (!connId) { setTables([]); return; }

    if (isExcelDataset) {
      setTables([]);
      setTablesError("");
      setColumns([]);
      setColumnsError("");
      return;
    }

    setColumns([]);
    setColumnsError("");
    setSearch("");
    setPage(1);

    if (skipInitialConnectionReset.current) {
      skipInitialConnectionReset.current = false;
    } else {
      setForm((s) => ({
        ...s,
        base_table: "",
        base_schema: "",
        fields: [],
        joins: [],
      }));
      setJoinedColumns({});
    }

    if (TABLES_CACHE.has(connId)) {
      setTables(TABLES_CACHE.get(connId));
      setTablesError("");
      return;
    }
    setTablesLoading(true);
    setTablesError("");
    api.get(`/connections/${connId}/schema`, { params: { tables_only: true } })
      .then((r) => {
        const flat = (r.data?.schemas || []).flatMap((s) => (s.tables || []).map((t) => ({ schema: s.name, name: t.name, type: t.type })));
        TABLES_CACHE.set(connId, flat);
        setTables(flat);
      })
      .catch((e) => setTablesError(e?.response?.data?.detail || e.message))
      .finally(() => setTablesLoading(false));
  }, [form.connection_id]);

  const refreshTables = () => {
    if (!form.connection_id) return;
    TABLES_CACHE.delete(form.connection_id);
    // trigger reload by setting then resetting connection_id state — simpler: just inline
    setTablesLoading(true);
    setTablesError("");
    api.get(`/connections/${form.connection_id}/schema`, { params: { tables_only: true } })
      .then((r) => {
        const flat = (r.data?.schemas || []).flatMap((s) => (s.tables || []).map((t) => ({ schema: s.name, name: t.name, type: t.type })));
        TABLES_CACHE.set(form.connection_id, flat);
        setTables(flat);
      })
      .catch((e) => setTablesError(e?.response?.data?.detail || e.message))
      .finally(() => setTablesLoading(false));
  };

  // Filter + paginate
  const q = search.trim().toLowerCase();
  const filtered = q ? tables.filter((t) => t.name.toLowerCase().includes(q) || (t.schema || "").toLowerCase().includes(q)) : tables;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const selectTable = async (t) => {
    setForm((s) => ({
      ...s,
      base_table: t.name,
      base_schema: t.schema,
      fields: [],
      joins: [],
    }));
    setJoinedColumns({});
    setColumns([]); setColumnsError("");
    setColumnsLoading(true);
    try {
      const r = await api.get(`/connections/${form.connection_id}/tables/${encodeURIComponent(t.name)}/columns`, { params: t.schema ? { schema: t.schema } : {} });
      setColumns(r.data?.columns || []);
    } catch (e) {
      setColumnsError(e?.response?.data?.detail || e.message);
    } finally {
      setColumnsLoading(false);
    }
  };

  const selectedTableNames = [
    form.base_table,
    ...(form.joins || []).map((j) => j.right_table),
  ].filter(Boolean);

  const fieldNameFor = (tableName, colName) =>
    (form.joins || []).length ? `${tableName}.${colName}` : colName;

  const toggleField = (tableName, col) => {
    const fieldName = fieldNameFor(tableName, col.name);
    const exists = form.fields.find((f) => f.name === fieldName);

    if (exists) {
      setForm((s) => ({
        ...s,
        fields: s.fields.filter((f) => f.name !== fieldName),
      }));
    } else {
      setForm((s) => ({
        ...s,
        fields: [
          ...s.fields,
          {
            name: fieldName,
            display_name: col.name,
            data_type: numLike(col.data_type) ? "number" : col.data_type,
            role: numLike(col.data_type) ? "measure" : "dimension",
          },
        ],
      }));
    }
  };

  const addJoin = async (tableMeta) => {
    if (!form.base_table || selectedTableNames.includes(tableMeta.name)) return;

    setColumnsLoading(true);
    setColumnsError("");

    try {
      const response = await api.get(
        `/connections/${form.connection_id}/tables/${encodeURIComponent(tableMeta.name)}/columns`,
        { params: tableMeta.schema ? { schema: tableMeta.schema } : {} }
      );

      const rightCols = response.data?.columns || [];
      const leftCols = columns || [];
      const common = leftCols.find((l) => rightCols.some((r) => r.name === l.name));

      setJoinedColumns((current) => ({
        ...current,
        [tableMeta.name]: rightCols,
      }));

      setForm((current) => {
        const qualifiedExistingFields = current.fields.map((field) => {
          if (field.name.includes(".")) return field;
          return {
            ...field,
            name: `${current.base_table}.${field.name}`,
            display_name: field.display_name || field.name,
          };
        });

        return {
          ...current,
          fields: qualifiedExistingFields,
          joins: [
            ...(current.joins || []),
            {
              left_table: current.base_table,
              right_table: tableMeta.name,
              join_type: "left",
              on: [{
                left: common?.name || leftCols[0]?.name || "",
                right: common?.name || rightCols[0]?.name || "",
              }],
            },
          ],
        };
      });
    } catch (e) {
      setColumnsError(e?.response?.data?.detail || e.message);
    } finally {
      setColumnsLoading(false);
    }
  };

  const updateJoin = (index, patch) => {
    setForm((current) => ({
      ...current,
      joins: current.joins.map((join, i) =>
        i === index ? { ...join, ...patch } : join
      ),
    }));
  };

  const updateJoinCondition = (index, side, value) => {
    setForm((current) => ({
      ...current,
      joins: current.joins.map((join, i) => {
        if (i !== index) return join;
        return {
          ...join,
          on: [{
            ...(join.on?.[0] || {}),
            [side]: value,
          }],
        };
      }),
    }));
  };

  const removeJoin = (index) => {
    const removedTable = form.joins[index]?.right_table;

    setForm((current) => {
      const remainingJoins = current.joins.filter((_, i) => i !== index);

      let remainingFields = current.fields.filter(
        (field) => !field.name.startsWith(`${removedTable}.`)
      );

      if (remainingJoins.length === 0) {
        remainingFields = remainingFields.map((field) => ({
          ...field,
          name: field.name.startsWith(`${current.base_table}.`)
            ? field.name.slice(current.base_table.length + 1)
            : field.name,
        }));
      }

      return {
        ...current,
        joins: remainingJoins,
        fields: remainingFields,
      };
    });

    setJoinedColumns((current) => {
      const next = { ...current };
      delete next[removedTable];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        connection_id: form.connection_id,
        base_table: form.base_table,
        fields: form.fields,
        joins: form.joins || [],
        filters: dataset?.filters || [],
        custom_sql: form.custom_sql || null,
      };

      if (isEditing) {
        await api.put(`/datasets/${dataset.id}`, payload);
      } else {
        await api.post("/datasets", payload);
      }

      onClose();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30">
      <div className="bg-white w-[1180px] max-w-[96vw] max-h-[94vh] overflow-auto rounded-md border" style={{ borderColor: "var(--c-border)" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border)" }}>
          <div className="section-title">{isEditing ? "Edit dataset" : "New dataset"}</div>
          <button className="btn btn-ghost" onClick={onClose}><X size={14} strokeWidth={1.8} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="label">Dataset name</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ds-name" />
            </div>
            <div>
              <label className="label">Connection</label>
              <select className="select" value={form.connection_id} onChange={(e) => setForm({ ...form, connection_id: e.target.value })} data-testid="ds-conn">
                <option value="">— pick connection —</option>
                {conns.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.source_type})</option>)}
              </select>
            </div>
            {isExcelDataset ? (
              <div className="border rounded p-3 text-[12.5px]" style={{ borderColor: "var(--c-border)", background: "#fffbeb" }}>
                <div className="font-medium mb-2">Excel dataset source</div>
                <div className="space-y-1" style={{ color: "var(--c-text-muted)" }}>
                  <div>
                    File: <span className="font-mono" style={{ color: "var(--c-text)" }}>
                      {dataset?.source_meta?.filename || "Uploaded Excel file"}
                    </span>
                  </div>
                  <div>
                    Sheet: <span className="font-mono" style={{ color: "var(--c-text)" }}>
                      {dataset?.source_meta?.sheet || "Sheet1"}
                    </span>
                  </div>
                  <div>
                    Rows: <span style={{ color: "var(--c-text)" }}>
                      {dataset?.source_meta?.row_count ?? dataset?.row_count ?? "—"}
                    </span>
                  </div>
                  <div className="pt-1">
                    Refresh or replace this data from the Excel Uploads page.
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label mb-0">Base table {tables.length > 0 && <span style={{ color: "var(--c-text-muted)" }}>({filtered.length}/{tables.length})</span>}</label>
                  <button type="button" className="btn btn-ghost text-[11px]" onClick={refreshTables} disabled={!form.connection_id || tablesLoading} data-testid="ds-refresh-tables">{tablesLoading ? "Loading…" : "Refresh"}</button>
                </div>
                <input
                  className="input mb-2"
                  placeholder={tables.length ? "Search tables…" : (form.connection_id ? (tablesLoading ? "Loading tables…" : "Pick a connection first") : "Pick a connection first")}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  disabled={!tables.length}
                  data-testid="ds-table-search"
                />
                <div className="border rounded max-h-[230px] overflow-auto text-[12.5px]" style={{ borderColor: "var(--c-border)" }} data-testid="ds-table-list">
                  {tablesError && <div className="p-2 text-red-600">{tablesError}</div>}
                  {!tablesError && tablesLoading && <div className="p-2" style={{ color: "var(--c-text-muted)" }}>Loading tables…</div>}
                  {!tablesError && !tablesLoading && visible.length === 0 && <div className="p-2" style={{ color: "var(--c-text-muted)" }}>{tables.length ? "No tables match." : "Pick a connection to see tables."}</div>}
                  {!tablesError && !tablesLoading && visible.map((t) => (
                    <div
                      key={`${t.schema}.${t.name}`}
                      className={`flex items-center gap-1 px-1 ${form.base_table === t.name ? "bg-slate-100" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => selectTable(t)}
                        className="flex-1 text-left px-1 py-1 font-mono hover:bg-slate-50"
                        data-testid={`ds-table-row-${t.name}`}
                      >
                        <span style={{ color: "var(--c-text-muted)" }}>{t.schema}.</span>{t.name}
                      </button>

                      {form.base_table &&
                        form.base_table !== t.name &&
                        !selectedTableNames.includes(t.name) && (
                          <button
                            type="button"
                            className="btn btn-ghost text-[10px] px-2"
                            onClick={() => addJoin(t)}
                            title={`Join ${t.name}`}
                          >
                            + Join
                          </button>
                        )}

                      {selectedTableNames.includes(t.name) &&
                        form.base_table !== t.name && (
                          <span className="text-[10px] px-2" style={{ color: "var(--c-text-muted)" }}>
                            Joined
                          </span>
                        )}
                    </div>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-1 text-[12px]" style={{ color: "var(--c-text-muted)" }}>
                    <button type="button" className="btn btn-ghost text-[11px]" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="ds-table-prev">Prev</button>
                    <span>Page {safePage} of {totalPages}</span>
                    <button type="button" className="btn btn-ghost text-[11px]" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="ds-table-next">Next</button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-3">
            {(form.joins || []).length > 0 && (
              <div>
                <label className="label">
                  Table joins ({form.joins.length})
                </label>

                <div className="space-y-2 max-h-[210px] overflow-auto">
                  {form.joins.map((join, index) => {
                    const leftCols =
                      join.left_table === form.base_table
                        ? columns
                        : (joinedColumns[join.left_table] || []);
                    const rightCols = joinedColumns[join.right_table] || [];

                    return (
                      <div
                        key={`${join.right_table}-${index}`}
                        className="border rounded p-2 text-[12px]"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <select
                            className="select"
                            value={join.join_type || "left"}
                            onChange={(e) =>
                              updateJoin(index, { join_type: e.target.value })
                            }
                          >
                            <option value="left">LEFT JOIN</option>
                            <option value="inner">INNER JOIN</option>
                            <option value="right">RIGHT JOIN</option>
                          </select>

                          <select
                            className="select"
                            value={join.left_table}
                            onChange={(e) =>
                              updateJoin(index, { left_table: e.target.value })
                            }
                          >
                            {[form.base_table, ...form.joins
                              .slice(0, index)
                              .map((j) => j.right_table)]
                              .filter(Boolean)
                              .map((tableName) => (
                                <option key={tableName} value={tableName}>
                                  {tableName}
                                </option>
                              ))}
                          </select>

                          <span>→</span>

                          <div className="input bg-slate-50">
                            {join.right_table}
                          </div>

                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => removeJoin(index)}
                            title="Remove join"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <select
                            className="select"
                            value={join.on?.[0]?.left || ""}
                            onChange={(e) =>
                              updateJoinCondition(index, "left", e.target.value)
                            }
                          >
                            <option value="">Left column</option>
                            {leftCols.map((col) => (
                              <option key={col.name} value={col.name}>
                                {join.left_table}.{col.name}
                              </option>
                            ))}
                          </select>

                          <select
                            className="select"
                            value={join.on?.[0]?.right || ""}
                            onChange={(e) =>
                              updateJoinCondition(index, "right", e.target.value)
                            }
                          >
                            <option value="">Right column</option>
                            {rightCols.map((col) => (
                              <option key={col.name} value={col.name}>
                                {join.right_table}.{col.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="label">
                Fields ({form.fields.length} selected)
              </label>

              <div
                className="border rounded p-2 max-h-[340px] overflow-auto text-[12.5px]"
                style={{ borderColor: "var(--c-border)" }}
                data-testid="ds-columns-list"
              >
                {isExcelDataset ? (
                  <div className="space-y-1">
                    {form.fields.map((field) => (
                      <div
                        key={field.name}
                        className="flex items-center gap-2 py-1 px-1 rounded hover:bg-slate-50"
                      >
                        <span className="font-mono flex-1">
                          {field.label || field.display_name || field.name}
                        </span>
                        <span
                          className="text-[11px]"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          {field.data_type}
                        </span>
                        <span className="pill">
                          {field.role}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                {!form.base_table && (
                  <div style={{ color: "var(--c-text-muted)" }}>
                    Pick a base table to see columns.
                  </div>
                )}

                {form.base_table && columnsLoading && (
                  <div style={{ color: "var(--c-text-muted)" }}>
                    Loading columns…
                  </div>
                )}

                {columnsError && (
                  <div className="text-red-600">{columnsError}</div>
                )}

                {!columnsLoading &&
                  !columnsError &&
                  selectedTableNames.map((tableName) => {
                    const tableCols =
                      tableName === form.base_table
                        ? columns
                        : (joinedColumns[tableName] || []);

                    return (
                      <div key={tableName} className="mb-3">
                        <div
                          className="text-[11px] font-semibold uppercase mb-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          {tableName}
                          {tableName === form.base_table ? " · Base" : " · Joined"}
                        </div>

                        {tableCols.map((col) => {
                          const fieldName = fieldNameFor(tableName, col.name);
                          const selected = form.fields.some(
                            (field) => field.name === fieldName
                          );

                          return (
                            <label
                              key={`${tableName}.${col.name}`}
                              className="flex items-center gap-2 py-0.5 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleField(tableName, col)}
                              />
                              <span className="font-mono">{col.name}</span>
                              <span
                                className="ml-auto"
                                style={{ color: "var(--c-text-muted)" }}
                              >
                                {col.data_type}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: "var(--c-border)" }}>
          <button className="btn btn-ghost" onClick={onClose}>
            {isExcelDataset ? "Close" : "Cancel"}
          </button>
          {isExcelDataset ? (
            <button
              className="btn btn-primary"
              onClick={() => { window.location.href = "/uploads"; }}
            >
              Manage in Excel Uploads
            </button>
          ) : (
            <button className="btn btn-primary" onClick={save} disabled={!form.name || !form.connection_id || !form.base_table || !form.fields.length || busy} data-testid="ds-save">{busy ? "Saving…" : (isEditing ? "Update dataset" : "Save dataset")}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function numLike(t) {
  if (!t) return false;
  return /int|decimal|numeric|double|float|real|bigint|smallint|tinyint/.test(String(t).toLowerCase());
}
